import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const STORAGE_KEY = 'stockReceiptDraft';

// The one empty shape a fresh line item starts from. Splits/offage arrays are re-created per line so
// two lines never share the same array reference.
export const EMPTY_LINE = {
  ndc: '',
  lotNumber: '',
  // Re-key confirmation of lotNumber. Entry-only: it gates submit and is never sent to the API.
  confirmLotNumber: '',
  bud: null,
  // The BUD the user had typed before a lot lookup replaced it with the existing lot's date.
  // Display only — drives the "Replaced ..." helper text so the overwrite is never silent.
  budReplacedFrom: null,
  quantityOrdered: '',
  quantityShipped: '',
  totalQuantityReceived: '',
  splits: [],
  offageReasons: [],
};

// JSON.stringify turns a Date into an ISO string and drops the type, so the picker gets a string back
// on reload. Tag Date instances on the way out and revive them on the way in, keyed by shape rather
// than field name so BUD, received date, or any future date column all survive a refresh.
const DATE_TAG = '$date';

const encodeDates = (value) => {
  if (value instanceof Date) return { [DATE_TAG]: value.toISOString() };
  if (Array.isArray(value)) return value.map(encodeDates);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, encodeDates(v)]));
  }
  return value;
};

const decodeDates = (value) => {
  if (Array.isArray(value)) return value.map(decodeDates);
  if (value && typeof value === 'object') {
    if (typeof value[DATE_TAG] === 'string') return new Date(value[DATE_TAG]);
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, decodeDates(v)]));
  }
  return value;
};

// sessionStorage is per-tab and clears on tab close, so a draft never resurfaces in a new session.
// Any read/parse failure (private mode, corrupt value) falls back to a fresh draft.
const readStoredDraft = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? decodeDates(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const makeEmptyDraft = () => ({
  lines: [],
  ndcDrugName: {},
  ndcSelected: {},
  lineExpanded: {},
  vendorIsFromList: false,
  vendorForm: {},
});

const StockReceivingDraftContext = createContext(null);

export const StockReceivingDraftProvider = ({ children }) => {
  // Line ids must stay unique across a hydrate: a fresh page load resets any module counter to 0, so
  // without seeding from the restored ids a newly added line would collide with a restored one.
  const idSeqRef = useRef(0);

  const createLine = useCallback(() => {
    idSeqRef.current += 1;
    return { ...EMPTY_LINE, splits: [], offageReasons: [], id: idSeqRef.current };
  }, []);

  const [draft, setDraft] = useState(() => {
    const stored = readStoredDraft();
    if (stored?.lines?.length) {
      idSeqRef.current = Math.max(0, ...stored.lines.map((line) => line.id || 0));
      return stored;
    }
    idSeqRef.current = 0;
    const fresh = makeEmptyDraft();
    fresh.lines = [createLine()];
    return fresh;
  });

  // Live state is the source of truth for rendering; sessionStorage is a write-through backup that
  // only matters when memory is wiped (refresh, tab restore). One effect keeps them in sync.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(encodeDates(draft)));
    } catch {
      /* quota or serialization failure: the in-memory draft still works, just isn't refresh-safe. */
    }
  }, [draft]);

  const patch = useCallback(
    (key) => (updater) =>
      setDraft((prev) => ({
        ...prev,
        [key]: typeof updater === 'function' ? updater(prev[key]) : updater,
      })),
    []
  );

  const setLines = useMemo(() => patch('lines'), [patch]);
  const setNdcDrugName = useMemo(() => patch('ndcDrugName'), [patch]);
  const setNdcSelected = useMemo(() => patch('ndcSelected'), [patch]);
  const setLineExpanded = useMemo(() => patch('lineExpanded'), [patch]);
  const setVendorIsFromList = useMemo(() => patch('vendorIsFromList'), [patch]);
  const setVendorForm = useMemo(() => patch('vendorForm'), [patch]);

  const resetDraft = useCallback(() => {
    idSeqRef.current = 0;
    const fresh = makeEmptyDraft();
    fresh.lines = [createLine()];
    setDraft(fresh);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing persisted / storage unavailable */
    }
  }, [createLine]);

  // True when there is real unsubmitted content worth resuming — a single blank line and an empty
  // vendor form is not a draft, so a first-time open never shows the resume prompt.
  const hasDraft = useMemo(() => {
    const lineDirty = draft.lines.some(
      (line) =>
        line.ndc ||
        line.lotNumber ||
        line.bud ||
        String(line.quantityOrdered).trim() ||
        String(line.quantityShipped).trim() ||
        String(line.totalQuantityReceived).trim() ||
        line.splits?.length ||
        line.offageReasons?.length
    );
    const vendorDirty = Object.values(draft.vendorForm ?? {}).some(
      (value) => value != null && String(value).trim() !== ''
    );
    return lineDirty || vendorDirty;
  }, [draft]);

  const contextValue = useMemo(
    () => ({
      lines: draft.lines,
      ndcDrugName: draft.ndcDrugName,
      ndcSelected: draft.ndcSelected,
      lineExpanded: draft.lineExpanded,
      vendorIsFromList: draft.vendorIsFromList,
      vendorForm: draft.vendorForm,
      setLines,
      setNdcDrugName,
      setNdcSelected,
      setLineExpanded,
      setVendorIsFromList,
      setVendorForm,
      createLine,
      resetDraft,
      hasDraft,
    }),
    [
      draft,
      setLines,
      setNdcDrugName,
      setNdcSelected,
      setLineExpanded,
      setVendorIsFromList,
      setVendorForm,
      createLine,
      resetDraft,
      hasDraft,
    ]
  );

  return (
    <StockReceivingDraftContext.Provider value={contextValue}>
      {children}
    </StockReceivingDraftContext.Provider>
  );
};

StockReceivingDraftProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useStockReceivingDraft = () => {
  const ctx = useContext(StockReceivingDraftContext);
  if (!ctx)
    throw new Error('useStockReceivingDraft must be used inside StockReceivingDraftProvider');
  return ctx;
};
